import { StyleRoot } from "@redotech/react-util/style";
import { Alignment } from "@redotech/redo-model/email-builder";
import { defaultStarRatingWidget } from "@redotech/redo-model/review/star-rating-widget-schema";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import StarIcon from "@redotech/redo-web/arbiter-icon/misc_star_icon.svg";
import { ReviewsRating } from "@redotech/redo-web/five-star-row/reviews-rating";
import { Align, Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useContext } from "react";
import { createRoot } from "react-dom/client";
import { RedoShopifyRpcClientContextProvider } from "../client/redo-shopify-rpc-client";
import { log } from "../monitoring/log";
import { RedoRoot } from "../root";
import STYLE_MANAGER from "../style-manager";
import {
  StarRatingsContext,
  StarRatingsProvider,
  StarRatingWidgetContext,
} from "./context";

export function initialize(autocheck: WidgetAutocheck) {
  log("Initializing Review Stars");

  const elements = document.querySelectorAll("div.redo-review-stars");

  log(`Found ${elements.length} review stars containers`);

  if (elements.length === 0) {
    return;
  }

  const validElements: Array<{ element: Element; productId: string }> = [];
  elements.forEach((element) => {
    const productId = element.getAttribute("product_id");
    if (!productId) {
      log("Skipping element without product_id");
      return;
    }

    // Shopify applies display: none to empty divs
    (element as HTMLElement).style.display = "unset";

    validElements.push({ element, productId });
  });

  if (validElements.length === 0) {
    return;
  }

  const storeUrl =
    window.redoStorefront?.storeUrl ??
    window.Shopify?.shop ??
    window.Shopify?.Checkout?.apiHost;

  const baseUrl =
    window.redoStorefront?.env?.REDO_SHOPIFY_SERVER_URL ??
    process.env.REDO_SHOPIFY_SERVER_URL!;

  validElements.forEach(({ element, productId }) => {
    const root = createRoot(element);
    root.render(
      <StyleRoot container={element} styleManager={STYLE_MANAGER}>
        <RedoRoot extensionShopifyClient={undefined} storeUrl={storeUrl}>
          <RedoShopifyRpcClientContextProvider
            baseUrl={baseUrl}
            storeUrl={window.Shopify?.shop ?? window.Shopify?.Checkout?.apiHost}
            widgetId={window.redoWidgetId}
          >
            <StarRatingsProvider
              autocheck={autocheck}
              productIds={validElements.map((item) => item.productId)}
            >
              <RedoReviewStars productId={productId} />
            </StarRatingsProvider>
          </RedoShopifyRpcClientContextProvider>
        </RedoRoot>
      </StyleRoot>,
    );
  });
}

const alignmentToFlexAlign: Record<Alignment, Align> = {
  [Alignment.LEFT]: "flex-start",
  [Alignment.CENTER]: "center",
  [Alignment.RIGHT]: "flex-end",
};

function RedoReviewStars({ productId }: { productId: string }) {
  const ratings = useContext(StarRatingsContext);
  const widgetSettings = useContext(StarRatingWidgetContext);
  const rating = ratings?.[productId] ?? null;

  if (!ratings) {
    return <Text fontSize="sm">Loading ratings...</Text>;
  }

  // Use defaultStarRatingWidget when widgetSettings is null or undefined
  const config = widgetSettings || defaultStarRatingWidget;

  const iconSize = config.starSize;
  const starPadding = config.starPadding;
  const iconColors = {
    filled: config.filledStarColor,
    empty: config.emptyStarColor,
  };
  const starOutlineEnabled = config.starOutlineEnabled;
  const starOutlineColor = config.starOutlineColor;
  const starOutlineWidth = config.starOutlineWidth;
  const alignment = config.alignment;

  return (
    <Flex align="center" dir="column" gap="sm" py="sm" w="full">
      <Flex
        align={alignmentToFlexAlign[alignment]}
        dir="column"
        gap="sm"
        w="full"
      >
        <Flex align="center" dir="row">
          <ReviewsRating
            editable={false}
            Icon={StarIcon}
            iconColors={iconColors}
            iconSize={iconSize}
            rating={rating ? rating.rating : 0}
            starOutlineColor={starOutlineColor}
            starOutlineEnabled={starOutlineEnabled}
            starOutlineWidth={starOutlineWidth}
            starPadding={starPadding}
          />
          <Text fontSize="sm">{`(${rating ? rating.numReviews : 0})`}</Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
